import { SubunitData, UnitData } from 'course-types';

const CourseUtilityService = {
  /**
   * Trackable types used for progress and activity counts
   */
  isTrackableType(type: string): boolean {
    return type === 'quiz' || type === 'video' || type === 'document';
  },

  /**
   * Calculate the total number of trackable activities across all units (excludes resources)
   * @param units Array of [unitId, UnitData] tuples
   * @returns Total count of trackable activities across all units and subunits
   */
  calculateTotalActivities(units: Array<[string, UnitData]>): number {
    return units.reduce((total, [unitId, unit]) => {
      const unitActivities =
        (unit.activities?.filter((a) => this.isTrackableType(a.type)).length || 0) +
        Object.values(unit.subunits || {}).reduce(
          (sum, subunit) =>
            sum + (subunit.activities?.filter((a) => this.isTrackableType(a.type)).length || 0),
          0,
        );
      return total + unitActivities;
    }, 0);
  },

  /**
   * Calculate the total number of resources across all units
   */
  calculateTotalResources(units: Array<[string, UnitData]>): number {
    return units.reduce((total, [unitId, unit]) => {
      const unitResources =
        (unit.activities?.filter((a) => a.type === 'resource').length || 0) +
        Object.values(unit.subunits || {}).reduce(
          (sum, subunit) =>
            sum + (subunit.activities?.filter((a) => a.type === 'resource').length || 0),
          0,
        );
      return total + unitResources;
    }, 0);
  },

  /**
   * Calculate the total number of trackable activities for a single unit
   * @param unit UnitData object
   * @returns Total count of trackable activities in the unit and its subunits
   */
  calculateUnitActivities(unit: UnitData): number {
    return (
      (unit.activities?.filter((a) => this.isTrackableType(a.type)).length || 0) +
      Object.values(unit.subunits || {}).reduce(
        (sum, subunit) =>
          sum + (subunit.activities?.filter((a) => this.isTrackableType(a.type)).length || 0),
        0,
      )
    );
  },

  /**
   * Calculate the total number of resources for a single unit
   */
  calculateUnitResources(unit: UnitData): number {
    return (
      (unit.activities?.filter((a) => a.type === 'resource').length || 0) +
      Object.values(unit.subunits || {}).reduce(
        (sum, subunit) =>
          sum + (subunit.activities?.filter((a) => a.type === 'resource').length || 0),
        0,
      )
    );
  },

  /**
   * Calculate the total number of trackable activities for a single subunit
   */
  calculateSubunitActivities(subunit: SubunitData): number {
    return subunit.activities?.filter((a) => this.isTrackableType(a.type)).length || 0;
  },

  /**
   * Calculate the total number of resources for a single subunit
   */
  calculateSubunitResources(subunit: SubunitData): number {
    return subunit.activities?.filter((a) => a.type === 'resource').length || 0;
  },
};

export default CourseUtilityService;
